import { FunctionComponent, useContext, useEffect, useState } from "react";
import AppStateContext from "../../../contexts/AppStateContext";
import ShippingCard from "./Card/ShippingCard";
import { ShippingMethodType } from "../../../types/CartTypes";
import PostNord from "assets/img/postnord_erhverv.jpg";
import EasyPartz from "assets/img/easypartz.jpg";
import TNT from "assets/img/TNT.png";
import Modal from "components/Modals/Modal";
import {
  Button,
  Card,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import cardStyles from "../../../components/Card/css/card.module.css";

type DeliveryMethod = "delivery" | "pickup" | "own_delivery";

const DELIVERY_METHOD_OPTIONS: { value: DeliveryMethod; label: string }[] = [
  { value: "delivery", label: "Delivery (carrier label)" },
  { value: "pickup", label: "Pickup" },
  { value: "own_delivery", label: "Own delivery" },
];

type PropsType = {
  currentMethod?: string;
  updateShipping: (
    code: string,
    price?: number,
    deliveryMethod?: DeliveryMethod
  ) => void;
  shippingMethods: ShippingMethodType[];
  isCustomShippingMethod?: boolean;
  // The quote's delivery method, the custom modal's default.
  deliveryMethod?: string | null;
  disabled?: boolean;
};

const ShippingMethods: FunctionComponent<PropsType> = ({
  currentMethod,
  updateShipping,
  shippingMethods,
  isCustomShippingMethod,
  deliveryMethod,
  disabled = false,
}) => {
  const { strings } = useContext(AppStateContext);

  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const [customName, setCustomName] = useState<string>();
  const [customPrice, setCustomPrice] = useState<number>();
  const [customDelivery, setCustomDelivery] =
    useState<DeliveryMethod>("delivery");

  useEffect(() => {
    if (
      deliveryMethod === "delivery" ||
      deliveryMethod === "pickup" ||
      deliveryMethod === "own_delivery"
    ) {
      setCustomDelivery(deliveryMethod);
    }
  }, [deliveryMethod]);

  useEffect(() => {
    if (isCustomShippingMethod && currentMethod) {
      setCustomName(currentMethod);
    }
  }, [isCustomShippingMethod, currentMethod]);

  const getShippingText = (id: string) => {
    // Shipmondo v1 codes. The carrier is picked when the order is packed.
    if (id === "delivery") {
      return {
        title: "Delivery",
        description: "",
        timeText: "",
        logo: PostNord,
      };
    }
    if (id === "own_delivery") {
      return {
        title: "Own delivery",
        description: "",
        timeText: "",
        logo: EasyPartz,
      };
    }
    // Codes from before v1 still show on old quotes and orders.
    if (id.includes("postnord")) {
      return {
        title: strings.PostNordDelivery,
        description: strings.PostNordDeliveryText,
        timeText: strings.PostNordDeliveryTimeShort,
        logo: PostNord,
      };
    }
    if (id.includes("tnt_before_12")) {
      return {
        title: strings.TNT2TitelShort,
        description: strings.TNT2Body,
        timeText: strings.TNT2DeliveryTime,
        logo: TNT,
      };
    }
    if (id.includes("tnt_next_day")) {
      return {
        title: strings.TNT2TitelShort,
        description: strings.TNT3Body,
        timeText: strings.TNT3DeliveryTime,
        logo: TNT,
      };
    }
    return {
      title: strings.SelfCollectShort,
      description: strings.SelfCollectText,
      timeText: strings.SelfCollectTimeShort,
      logo: EasyPartz,
    };
  };

  return (
    <>
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <h2>Custom shipping</h2>
          <TextField
            label="Name"
            size="small"
            onChange={(e) => setCustomName(e.target.value)}
            value={customName ?? ""}
            required
          />
          <TextField
            label="Price (DKK)"
            size="small"
            type="number"
            onChange={(e) => setCustomPrice(Number(e.target.value))}
            value={customPrice ?? ""}
            required
          />
          <FormControl size="small">
            <InputLabel>Sent as</InputLabel>
            <Select
              label="Sent as"
              value={customDelivery}
              onChange={(e) =>
                setCustomDelivery(e.target.value as DeliveryMethod)
              }
            >
              {DELIVERY_METHOD_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <Button variant="outlined" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if (customName?.trim() && customPrice !== undefined) {
                  setModalOpen(false);
                  updateShipping(
                    customName.trim(),
                    customPrice,
                    customDelivery
                  );
                }
              }}
            >
              Save
            </Button>
          </div>
        </div>
      </Modal>
      <Card className={cardStyles.card}>
        <div style={{ margin: "1rem" }}>
          <span style={{ fontWeight: "bold", fontSize: "1.1rem" }}>
            Shipping
          </span>
        </div>
        <div style={{ margin: "1rem" }}>
          <Grid
            container={true}
            style={{ display: "flex", flexWrap: "nowrap" }}
          >
            <div style={{ display: "flex", flexDirection: "row" }}>
              {shippingMethods.map((item, key) => {
                const shippingTexts = getShippingText(item.code ?? "");
                return (
                  <Grid key={key} item style={{ width: "100%" }}>
                    <div style={{ paddingRight: "0.5rem" }}>
                      <ShippingCard
                        shippingTitle={shippingTexts.title}
                        shippingPrice={item.price > 0 ? item.price : undefined}
                        priceText={
                          item.price_on_request ? "Price on request" : undefined
                        }
                        isSelected={currentMethod === item.code}
                        onClick={() => updateShipping(item.code)}
                        disabled={disabled && currentMethod !== item.code}
                      />
                    </div>
                  </Grid>
                );
              })}
              <Grid key={"customShipping"} item style={{ width: "100%" }}>
                <div style={{ paddingRight: "0.5rem" }}>
                  <ShippingCard
                    shippingTitle={customName ?? "Custom"}
                    shippingPrice={customPrice}
                    isSelected={!!isCustomShippingMethod}
                    onClick={() => !disabled && setModalOpen(true)}
                    disabled={disabled}
                  />
                </div>
              </Grid>
            </div>
          </Grid>
        </div>
      </Card>
    </>
  );
};

export default ShippingMethods;
